<template>
    <div>
        <div class="card product-tile mb-4 shadow-sm" v-if="!isLoading">
            <div class="card-body">
                <b-link
                 :to="$getUrl(
                    'pdp', {
                        slug: entry.slug,
                        productUuid: entry.vehicle_uuid,
                    }, getQueryObject
                 )"
                >
                    <div class="row py-1">
                        <div class="col-12 col-md-5">
                            <b-badge class="image-badge" :variant="isInstantAvailable ? 'primary' : 'secondary'">{{ getAvailableText }}</b-badge>
                            <b-badge v-if="entry.tier" class="image-badge" :variant="'secondary'">{{ entry.tier.title }}</b-badge>
                            <div class="image" :class="imageClass">
                                <img class="img-fluid" :src="getImage" :alt="getAlt" />
                            </div>
                        </div>
                        <div class="col-12 col-md-7">
                            <div class="title-box pb-2">
                                <h3 class="mb-0 mt-2">{{ entry.brand.title }} {{ entry.model.title }}</h3>
                                <span>{{ entry.description }}</span>
                            </div>
                            <div class="product-data-box mb-2">
                                <p class="mb-1">
                                    <span class="no-wrap-string">{{ getPower }}</span>
                                    <span class="no-wrap-string">• {{ $t('general.gearshift.' + entry.gearshift.slug) }}</span>
                                    <span class="no-wrap-string">• {{ $t('general.fuel.' +entry.fuel.slug) }}</span>
                                    <span class="no-wrap-string">• {{ getCurrentMiles }}</span>
                                    <span class="no-wrap-string">• {{ getFirstRegistrationYear }}</span>
                                    <vehicle-card-consumption v-if="showConsumption" :data="entry.consumption"></vehicle-card-consumption>
                                </p>
                            </div>
                            <space-break class="pb-2"></space-break>
                            <div class="product-price-box">
                                <p class="mb-0 text-primary">
                                    <b>{{ this.$root.whitelabel.getFormatetPrice(price) }}
                                        <span v-if="withoutTax">{{$t('cat.product_tile.net_per_month')}}</span>
                                        <span v-else>{{$t('cat.product_tile.per_month')}}</span>
                                    </b>
                                </p>
                                <span v-html="mileageText"></span>
                                <location :vehicleLocation="entry.location"></location>
                            </div>
                        </div>
                    </div>
                </b-link>
            </div>
        </div>
        <div class="card product-title mb-4" v-else>
            <div class="card-body">
                <div class="row">
                    <div class="col-12 col-md-5">
                        <b-skeleton-img width="100%"></b-skeleton-img>
                    </div>
                    <div class="col-12 col-md-7">
                        <div class="title-box pb-1 mt-2 mt-md-0">
                            <b-skeleton height="24px" width="30%"></b-skeleton>
                            <b-skeleton height="19px" width="20%"></b-skeleton>
                        </div>
                        <div class="product-data-box">
                            <b-skeleton height="19px" width="50%" class="my-2"></b-skeleton>
                            <b-skeleton height="19px" width="80%"></b-skeleton>
                        </div>
                        <space-break class="py-1 mt-3"></space-break>
                        <div class="product-price-box">
                            <b-skeleton height="24px" width="25%"></b-skeleton>
                            <b-skeleton height="19px" width="40%"></b-skeleton>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import { BLink, BBadge, BSkeleton, BSkeletonImg } from "bootstrap-vue"
import SpaceBreak from "../atoms/SpaceBreak.vue"
import VehicleCardConsumption from "../atoms/VehicleCardConsumption.vue"
import Location from "../atoms/Location.vue"
import { gb } from "../../growthbook";

export default {
    name: 'ProductTile',
    components: {
        SpaceBreak,
        BLink, BBadge, BSkeleton, BSkeletonImg, VehicleCardConsumption, Location
    },
    data(){
        return {
            price: {
                type: Number
            },
            cheapestOption: {
                type: Object
            }
        }
    },
    props: {
        isLoading: {
            type: Boolean,
        },
        entry: {
            type: Object
        },
        powerType: {
            type: String
        }
    },
    methods: {
        getLowestPriceAndMileage() {
            let selectedMileage = null;
            let selectedDuration = null;
            let priceField = this.$root.whitelabel.setting('show_price_without_taxes') ? 'price_without_tax' : 'price'

            let cheapestOption = this.entry['price_plans'];
            if(cheapestOption){
                if(selectedDuration !== null) {
                    cheapestOption = cheapestOption.filter(item => item.duration_id === selectedDuration);
                }
                if(selectedMileage !== null) {
                    cheapestOption = cheapestOption.filter(item => item.mileage_id === selectedMileage);
                }

                cheapestOption = cheapestOption.reduce((prev, curr) => prev[priceField] < curr[priceField] ? prev : curr)

                this.cheapestOption = cheapestOption;
                this.price = cheapestOption[priceField]
            }
        },
    },
    computed: {
        mileageText() {
            const totalMileage = this.cheapestOption.mileage * this.cheapestOption.minimum_duration;
            const top = `${this.$t('cat.product_tile.mileage.text')} ${this.cheapestOption.minimum_duration} ${this.cheapestOption.duration_id === 1 ? this.$t('cat.product_tile.month') : this.$t('cat.product_tile.months2')} & ${this.$n(this.cheapestOption.mileage, 'number')} ${this.$root.whitelabel.getMileageUnit()} ${this.$t('pds.buy_box.mileage_for_text')}`;
            const bottom = `<br> (${this.$t('cat.product_tile.total_milage_text')}: ${this.$n(totalMileage, 'number')} ${this.$root.whitelabel.getMileageUnit()})`;

            return `${top}${bottom}`
        },
        imageClass(){
            return this.$root.whitelabel.setting('catalog_image_fit') ? 'object-fit-cover' : 'object-fit-contain'
        },
        getAvailableText(){
            if(this.entry.available_status == 'COMING_SOON') {
                return this.$t('general.availability.coming_soon')
            } else if (this.entry.available_status == 'NOW_AVAILABLE') {
                return this.$t('general.availability.now_available')
            } else {
                if(gb.isOn("vehicle_availibility_updated")) {
                    return this.$t('vehicle.availability.text.' + this.entry.available_text)
                } else {
                    return this.entry.available_weeks === 1 ? `${this.$t('general.availability.week', {number: this.entry.available_weeks})}` : `${this.$t('general.availability.weeks', {number: this.entry.available_weeks})}`
                }                
            }
        },
        isInstantAvailable(){
            if(this.entry.available_status == 'COMING_SOON') {
                return false
            } else if (this.entry.available_status == 'NOW_AVAILABLE') {
                return true
            } else {
                if(gb.isOn("vehicle_availibility_updated")) {
                    if(this.entry.available_text === 'shortly') {
                        return true
                    }
                    return false
                } else {
                    return this.entry.available_weeks <= 2
                }   
            }
        },
        getPower: function () {
            return (this.powerType === 'PS' || this.powerType === 'HP') ? `${this.entry.power.hp} ${this.$t('cat.product_tile.power.unit')}` : `${this.entry.power.kw} ${this.$t('cat.product_tile.kW')}`
        },
        getFirstRegistrationYear: function () {
            return this.entry.first_registration ? `${this.$t('cat.product_tile.first_registration')} ${this.entry.first_registration.substring(0, 4)}` : ''
        },
        getFuelConsumptionUnit() {
            return (this.entry.fuel.slug === 'strom-elektro' || this.entry.fuel.slug === 'elektro') ? this.$t('cat.product_tile.consumption.electric.unit') : this.$t('cat.product_tile.consumption')
        },
        getImage: function (){
            return this.entry && this.entry.title_image ? this.entry.title_image : this.$root.whitelabel.getDefaultImage()

        },
        getAlt: function() {
            return this.entry && this.entry.title_image ? '' : this.$t('image.alt.noimage')
        },
        getCurrentMiles: function (){
            if(this.entry){
                const number = this.entry.current_miles.replace(' Km','')
                return `${this.$n(parseInt(number), 'number')} ${this.$root.whitelabel.getMileageUnit()}`
            }
            return ''
        },
        getQueryObject(){
            let object = {
                SID: this.entry.subsidiary_uuid,
                VID: this.entry.id,
                path: ''
            }

            if(Object.keys(this.$route.query).length){
                object.path = JSON.stringify(this.$route.query)
            }

            return object
        },
        showConsumption() {
            if(this.$root.whitelabel.state.contact.address.country === 'us' || this.entry.category.parent_type == 'Motorbike') {
                return false
            }
            return true
        },
        withoutTax() {
            return this.$root.whitelabel.setting('show_price_without_taxes')
        }
    },
    created() {
        this.getLowestPriceAndMileage()
    }
}
</script>
